import React, { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { FadeTransition } from "../../../pages/get-compliant/FadeTransition";
import { BaseQuestion, BaseQuestionProps } from "./BaseQuestion";
import { TextField } from "@mui/material";
import _ from "lodash";
import { QuestionLabel } from "./QuestionContainer";
import { WYSIWYGEditor } from "../../../../common/components/input/WYSIWYGEditor";
import { DisableableButton } from "../../Disableable";
import clsx from "clsx";

export type SurveyFreetextQuestionProps = BaseQuestion & {
  type: "freetext";
  wysiwyg?: boolean;
  validateFreetext?: (answer) => string | null;
  emptyText?: string | ReactNode;
  rows?: number;
  toolbar?: boolean;
  showSaveButton?: boolean;
};

type Props = BaseQuestionProps & {
  question: SurveyFreetextQuestionProps;
};

export const SurveyFreetextQuestion: React.FC<Props> = ({
  question,
  answer,
  onAnswer,
  collapseTopSpacing,
}) => {
  const { showSaveButton } = question;
  const saveOnChange = !showSaveButton;

  const [localValue, setLocalValue] = useState(answer);

  // Store onAnswer in a ref so it's always current but doesn't cause re-renders
  const onAnswerRef = useRef(onAnswer);
  useEffect(() => {
    onAnswerRef.current = onAnswer;
  }, [onAnswer]);

  // Create a stable debounced function
  const debouncedAnswerRef = useRef(
    _.debounce((value: string) => {
      onAnswerRef.current(value);
    }, 400),
  );

  const handleChange = ({ target }) => {
    setLocalValue(target.value);
    if (saveOnChange) {
      debouncedAnswerRef.current(target.value);
    }
  };

  // Provide stable WYSIWYG handlers like Privacy Policy page pattern
  const wysiChangeRef = useRef<(contents: string) => void>(() => {});
  useEffect(() => {
    wysiChangeRef.current = (contents: string) => {
      setLocalValue(contents);
      if (saveOnChange) {
        debouncedAnswerRef.current(contents);
      }
    };
  }, [saveOnChange]);

  const stableWysiOnChange = useCallback((contents: string) => {
    wysiChangeRef.current(contents);
  }, []);

  const wysiBlurRef = useRef<(event: FocusEvent, contents: string) => void>(() => {});
  useEffect(() => {
    wysiBlurRef.current = (_event: FocusEvent, contents: string) => {
      setLocalValue(contents);
      if (saveOnChange) {
        // Flush immediately on blur
        onAnswerRef.current(contents);
      }
    };
  }, [saveOnChange]);

  const stableWysiOnBlur = useCallback((event: any, contents: string) => {
    // Redactor passes (event, { html }) but our wrapper sends contents already
    wysiBlurRef.current(event as unknown as FocusEvent, contents);
  }, []);

  if (question.notPresent) {
    return null;
  }

  return (
    <FadeTransition
      key={`question-${question.slug}`}
      cls="survey-question-fade"
      in={!!answer || question.visible}
      unmountOnExit
      appear
    >
      <div
        className={clsx("survey--question", { "survey-question--top-margin": !collapseTopSpacing })}
        ref={question.ref}
      >
        <QuestionLabel
          label={question.question}
          helpText={question.helpText}
          header={question.header}
          description={question.description}
        />

        {question.wysiwyg && (
          <WYSIWYGEditor
            height="250px"
            rows={question.rows}
            toolbar={question.toolbar}
            defaultValue={answer ?? ""}
            onChange={stableWysiOnChange}
            onBlur={stableWysiOnBlur}
          />
        )}

        {!question.wysiwyg && (
          <TextField
            className="freetext--textfield"
            variant="outlined"
            value={localValue ?? ""}
            fullWidth={true}
            onChange={handleChange}
            multiline={true}
            rows={question.rows ?? 3}
          />
        )}
        {showSaveButton && (
          <div className="d-flex justify-end mt-md">
            <DisableableButton
              variant="contained"
              color="primary"
              className="min-width-128"
              disabled={answer === localValue}
              disabledTooltip="You have no unsaved changes."
              onClick={() => {
                onAnswerRef.current(localValue ?? "");
              }}
            >
              Save
            </DisableableButton>
          </div>
        )}
      </div>
    </FadeTransition>
  );
};
